import { Fragment, useEffect, useMemo, useState } from 'react'
import { Divider, Grid } from 'semantic-ui-react'
import { FormikProvider, useFormik } from 'formik'
import { faLandmark, faHome } from '@fortawesome/pro-light-svg-icons'
import { faUpload } from '@fortawesome/pro-regular-svg-icons'
import {
  Alert,
  Button,
  Card,
  FormikInput,
  FormikLabelError,
  FormikRadioToggleButton,
  GridRowColumn,
  Icon,
  Link,
  makeNumberSchema,
  makeReqBoolSchema,
  Modal,
  Text,
} from '@/componentsV1/BaseComponents'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import UploadedDocument from '../tax-checklist/documents/UploadedDocument'
import FileUploadModal from '@/componentsV1/FileUpload/FileUploadModal'
import { centsToDollars, dollarsToCents } from '@/utils/currencyHelpers'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { UploadDocumentType } from '@/constants/businessConstants'
import { useReselector } from '@/utils/sharedHooks'
import { stateAbbToState } from '@/constants/locationConstants'
import { getFetchError } from '@/reducers/fetch'
import {
  AnnualTaxOutcome,
  UPDATE_ANNUAL_TAX_OUTCOME_KEY,
  updateAnnualTaxOutcome,
} from '../annualTaxOutcome.slice'
import {
  getOutcomeFormValues,
  getPreparerEstimateText,
  isShowingPreparerFigure,
} from '../annualTaxOutcome.helpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { fetchTaxUserDocumentsForYearIfNeeded } from '../taxUserDocuments.slice'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'

export const AnnualTax1040WrapUpModal = ({
  onClose,
  modalOpen,
  year,
  outcomes,
  extensionModal = false,
  paymentDueDate,
}: {
  onClose: () => void
  modalOpen: boolean
  year?: string
  outcomes: AnnualTaxOutcome[]
  extensionModal?: boolean
  paymentDueDate?: string | null
}) => {
  const dispatch = useAppDispatch()
  const [modalOpenForIndex, setModalOpenForIndex] = useState<number>()
  const error = useReselector(getFetchError, UPDATE_ANNUAL_TAX_OUTCOME_KEY)
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const yearToUse = year || currentTaxYear
  const formattedPaymentDueDate = paymentDueDate
    ? convertUtcToLocalDate(paymentDueDate)?.toFormat(
        DATE_FORMATS_LUXON.DISPLAY_LONG_SHORT_DAY
      )
    : `April 15, ${Number(yearToUse) + 1}`

  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchTaxUserDocumentsForYearIfNeeded(yearToUse))
  }, [dispatch, yearToUse])

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      outcomes: outcomes.map((outcome) =>
        getOutcomeFormValues(outcome, { extensionModal })
      ),
    },
    onSubmit: async (data) => {
      const results = await Promise.all(
        data.outcomes
          .filter((outcome) => !outcome.notApplicable)
          .map(
            (outcome) =>
              outcome.isRefund !== null &&
              updateAnnualTaxOutcome(outcome.id, {
                isRefund: outcome.isRefund,
                receiptDocumentId: outcome.receiptDocumentId,
                amountInCents: dollarsToCents(outcome.amount),
              })(dispatch)
          )
      )

      if (results.every((res) => res)) {
        onClose()
      }
    },
  })

  const {
    values,
    submitForm,
    setFieldValue,
    validateForm,
    isValid,
    isSubmitting,
  } = formik

  const content = useMemo(() => {
    return (
      <Modal.Content>
        <Grid>
          <GridRowColumn>
            {extensionModal ? (
              <>
                <Text as="h3">
                  Reminder: An extension to file is not an extension to pay
                </Text>
                <Text style={{ marginTop: 8 }}>
                  To avoid penalties for underpaying, a payment should be made
                  by <b>{formattedPaymentDueDate}.</b> Check out this{' '}
                  <Link
                    as="secondaryLink"
                    href="https://support.joinheard.com/hc/en-us/articles/31968570013335-After-tax-extension-filing"
                    newPage
                  >
                    article
                  </Link>{' '}
                  for advice on how much to pay and more.
                </Text>
                <Alert type="info" style={{ marginTop: 16 }}>
                  <Text as="bodySm" style={{ fontWeight: 600 }}>
                    How much should I pay?
                  </Text>
                  <Text as="bodySm" style={{ marginTop: 4 }}>
                    Paying at least 100% of last year&apos;s tax liability (or
                    110% if you earned over $150,000) protects you from
                    underpayment penalties until you file.
                  </Text>
                </Alert>
              </>
            ) : (
              <Text>
                For each entity, please let us know if you received a refund or
                if you made a tax payment.
              </Text>
            )}
          </GridRowColumn>
          {error && (
            <GridRowColumn>
              <Alert type="error">{error.message}</Alert>
            </GridRowColumn>
          )}
          {values.outcomes.map((result, i) => {
            const { id, isRefund, isFederal, receiptDocumentId, state } = result
            const preparerEstimate = extensionModal
              ? getPreparerEstimateText(result)
              : null
            const title = isFederal
              ? 'federal tax'
              : `${state ? stateAbbToState(state) : ''} state tax`

            return (
              <Fragment key={id}>
                {i !== 0 && (
                  <GridRowColumn>
                    <Divider />
                  </GridRowColumn>
                )}
                <Grid.Row key={id}>
                  <Grid.Column width={1}>
                    <Icon
                      icon={isFederal ? faLandmark : faHome}
                      color="green"
                      size="2x"
                    />
                  </Grid.Column>

                  <Grid.Column width={15}>
                    <Grid>
                      <GridRowColumn>
                        <Text as="eyebrow" color="darkGray">
                          {title}
                        </Text>
                      </GridRowColumn>
                      {!result.notApplicable && preparerEstimate && (
                        <GridRowColumn short>
                          <Text>{preparerEstimate}</Text>
                        </GridRowColumn>
                      )}
                      <GridRowColumn short>
                        {result.notApplicable ? (
                          <Card backgroundColor="stone" variant="noShadow">
                            <Text color="oak">
                              Your state does not require payment of income
                              taxes.
                            </Text>
                          </Card>
                        ) : (
                          <FormikLabelError
                            name={`outcomes[${i}].isRefund`}
                            label={
                              extensionModal
                                ? `Did you make an estimated payment for annual ${isFederal ? 'Federal' : `${state ? stateAbbToState(state) : ''} state`} taxes?`
                                : `For your personal ${title} return, you:`
                            }
                            schema={makeReqBoolSchema()}
                          />
                        )}
                      </GridRowColumn>
                      {!result.notApplicable && extensionModal && (
                        <Grid.Row className="short">
                          <Grid.Column width={8}>
                            <FormikRadioToggleButton
                              name={`outcomes[${i}].isRefund`}
                              value={false}
                              fullWidth
                              onClick={() => {
                                if (
                                  (result.amount === '' ||
                                    result.amount === 0) &&
                                  result.preparerAmountInCents
                                ) {
                                  setFieldValue(
                                    `outcomes[${i}].amount`,
                                    centsToDollars(result.preparerAmountInCents)
                                  )
                                }
                                validateForm()
                              }}
                            >
                              Yes
                            </FormikRadioToggleButton>
                          </Grid.Column>
                          <Grid.Column width={8}>
                            <FormikRadioToggleButton
                              name={`outcomes[${i}].isRefund`}
                              value
                              fullWidth
                              onClick={() => {
                                setFieldValue(`outcomes[${i}].amount`, 0)
                                setFieldValue(
                                  `outcomes[${i}].receiptDocumentId`,
                                  null
                                )
                              }}
                            >
                              No
                            </FormikRadioToggleButton>
                          </Grid.Column>
                        </Grid.Row>
                      )}
                      {!result.notApplicable && !extensionModal && (
                        <Grid.Row className="short">
                          <Grid.Column width={8}>
                            <FormikRadioToggleButton
                              name={`outcomes[${i}].isRefund`}
                              value
                              fullWidth
                            >
                              Received a Refund
                            </FormikRadioToggleButton>
                          </Grid.Column>
                          <Grid.Column width={8}>
                            <FormikRadioToggleButton
                              name={`outcomes[${i}].isRefund`}
                              value={false}
                              fullWidth
                              onClick={validateForm}
                            >
                              Paid Taxes
                            </FormikRadioToggleButton>
                          </Grid.Column>
                        </Grid.Row>
                      )}
                      {!result.notApplicable &&
                        isRefund !== null &&
                        (!extensionModal || isRefund === false) && (
                          <>
                            <GridRowColumn>
                              <FormikInput
                                label={
                                  extensionModal
                                    ? 'How much did you pay?'
                                    : isRefund
                                      ? 'How much was your refund?'
                                      : `How much did you pay in ${title}es?`
                                }
                                componentType="currency"
                                name={`outcomes[${i}].amount`}
                                fullWidth
                                schema={makeNumberSchema({
                                  allowedDecimals: 2,
                                })}
                              />
                            </GridRowColumn>
                            {isShowingPreparerFigure(result) && (
                              <GridRowColumn>
                                <Text as="bodySm" color="darkGray">
                                  Filled in from your tax pro&apos;s figures.
                                  Check them before updating.
                                </Text>
                              </GridRowColumn>
                            )}
                            {extensionModal && isFederal && (
                              <GridRowColumn>
                                <Text as="bodySm" color="darkGray">
                                  You can find your tax liability on Line 24 of
                                  last year&apos;s federal return.
                                </Text>
                              </GridRowColumn>
                            )}
                          </>
                        )}
                      {!result.notApplicable && isRefund === false && (
                        <>
                          <GridRowColumn verticalAlign="middle">
                            <Button
                              variant="secondary"
                              onClick={() => setModalOpenForIndex(i)}
                            >
                              <Icon
                                icon={faUpload}
                                size="1x"
                                style={{ marginRight: 10 }}
                              />
                              Upload payment receipt
                            </Button>
                          </GridRowColumn>
                          {receiptDocumentId && (
                            <UploadedDocument id={receiptDocumentId} />
                          )}
                        </>
                      )}
                    </Grid>
                  </Grid.Column>
                </Grid.Row>
              </Fragment>
            )
          })}
        </Grid>
      </Modal.Content>
    )
  }, [
    error,
    validateForm,
    values.outcomes,
    extensionModal,
    setFieldValue,
    formattedPaymentDueDate,
  ])

  return (
    <FormikProvider value={formik}>
      <Modal open={modalOpen} closeIcon onClose={onClose} size="small">
        <Modal.Header>
          {yearToUse} Annual Tax {extensionModal ? 'Extension' : 'Season'}{' '}
          Wrap-Up
        </Modal.Header>
        {content}
        <Modal.Actions>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submitForm}
            loading={isSubmitting}
            disabled={!isValid || isSubmitting}
          >
            Update
          </Button>
        </Modal.Actions>
      </Modal>

      <FileUploadModal
        open={modalOpenForIndex !== undefined}
        close={() => setModalOpenForIndex(undefined)}
        userFacing
        documentType={UploadDocumentType.TAX}
        categoryInternalName={
          UserDocumentCategoryIdentifier.annualTaxPaymentReceipt
        }
        setUploadedFile={async ({ id }) => {
          await setFieldValue(
            `outcomes[${modalOpenForIndex}].receiptDocumentId`,
            id
          )
        }}
        year={yearToUse}
      />
    </FormikProvider>
  )
}
